import type { SelectInternalTerminal } from '@workspace/db/extra'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  cashDrawerError,
  useAssignTerminal,
  useCashDrawerConfig,
  useLocationTerminals,
  usePrinterConfig,
  useSetupCashDrawer,
  useSetupReceiptPrinter,
  useTerminalSearch,
  useUnassignCashDrawer,
  useUnassignReceiptPrinter,
  useUnassignTerminal,
} from '../../../features/locations/use-pos-terminals'
import { useIdOrCode } from '../../use-id-or-code'
import { Btn, Chip, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, Select, SubCrumbs, td, th } from './controls'
import { Bar, Dot, mono, Note, Panel, SkelPanels } from './ui'

const COOLDOWN_S = 90
const termTone = (s: string | null) => (s === 'online' ? 'ok' : s === 'offline' ? 'bad' : 'off')

/** Client-side stand-in for v1's 90s per-workstation KV cooldown (the server lock is not built in v2). */
function useCooldown() {
  const [until, setUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (until <= Date.now()) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [until])
  return {
    left: Math.max(0, Math.ceil((until - now) / 1000)),
    start: () => {
      setNow(Date.now())
      setUntil(Date.now() + COOLDOWN_S * 1000)
    },
  }
}

function AssignDialog({
  idOrCode,
  storeCode,
  banner,
  assignedIds,
  onClose,
}: {
  idOrCode: string
  storeCode: string
  banner: string
  assignedIds: Set<string>
  onClose: () => void
}) {
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const search = useTerminalSearch(banner, q)
  const assign = useAssignTerminal()
  const hits = (search.data ?? []).filter((t) => !assignedIds.has(t.id))

  return (
    <Dialog open title="Assign terminal" onClose={onClose} footer={<Btn onClick={onClose}>Done</Btn>}>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(draft.trim())
        }}
      >
        <input
          className={inputCls}
          placeholder="Search by terminal name"
          aria-label="Search terminals"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={mono}
        />
        <Btn type="submit" variant="primary" disabled={draft.trim() === ''}>
          Search
        </Btn>
      </form>
      {search.isFetching && <Inline tone="muted">searching...</Inline>}
      {search.error && <Inline tone="bad">{apiErrorMessage(search.error)}</Inline>}
      {q && !search.isFetching && hits.length === 0 && <Inline tone="muted">no unassigned terminals match</Inline>}
      <ul className="border border-[var(--color-border)] empty:hidden" style={{ borderRadius: 'var(--radius)' }}>
        {hits.map((t) => (
          <li
            key={t.id}
            className="flex h-8 items-center justify-between gap-3 border-b border-[var(--color-border)] px-2 text-[12px] last:border-b-0"
          >
            <span className="min-w-0 truncate">
              {t.name}
              <span className="ml-2 text-[var(--color-muted)]" style={mono}>
                {[t.model, t.serialNumber].filter(Boolean).join(' · ')}
              </span>
            </span>
            <Btn disabled={assign.isPending} onClick={() => assign.mutate({ idOrCode, terminalId: t.id, storeCode })}>
              Assign
            </Btn>
          </li>
        ))}
      </ul>
      {assign.error && <Inline tone="bad">{apiErrorMessage(assign.error)}</Inline>}
      {assign.isSuccess && <Inline tone="ok">terminal assigned</Inline>}
    </Dialog>
  )
}

const PRINTER_MODELS: [string, string][] = [
  ['TM-H6000IV', 'TM-H6000IV (USB)'],
  ['TM-88V', 'TM-88V (Network)'],
  ['Toshiba6145', 'Toshiba 6145'],
]
const LOGICAL = ['CashDrawer1', 'CashDrawer2', 'CashDrawer3', 'CashDrawer4'] as const

function PeripheralsDialog({
  code,
  terminal,
  onClose,
}: {
  code: string
  terminal: SelectInternalTerminal
  onClose: () => void
}) {
  const ws = terminal.workstationId ?? null
  const printer = usePrinterConfig(code, ws)
  const drawer = useCashDrawerConfig(code, ws)
  const setupPrinter = useSetupReceiptPrinter()
  const unassignPrinter = useUnassignReceiptPrinter()
  const setupDrawer = useSetupCashDrawer()
  const unassignDrawer = useUnassignCashDrawer()
  const cooldown = useCooldown()

  const [model, setModel] = useState('')
  const [mac, setMac] = useState('')
  const [size, setSize] = useState<'' | 'large' | 'small'>('')
  const [ptype, setPtype] = useState<'' | 'epson' | 'toshiba'>('')
  const [logical, setLogical] = useState<(typeof LOGICAL)[number]>('CashDrawer1')
  const [smallOk, setSmallOk] = useState(false)

  const locked = cooldown.left > 0
  const printerValid = model !== '' && (model !== 'TM-88V' || mac.trim() !== '')
  const drawerRuleError = size ? cashDrawerError({ drawerSize: size, printerType: ptype }) : null
  const drawerValid = size !== '' && !drawerRuleError && (size !== 'small' || smallOk)

  if (!ws)
    return (
      <Dialog
        open
        title={`Peripherals - ${terminal.name}`}
        onClose={onClose}
        footer={<Btn onClick={onClose}>Close</Btn>}
      >
        <p className="text-[12px] text-[var(--color-muted)]">
          No workstation number yet, so a receipt printer or cash drawer cannot be configured. Register the terminal as
          a workstation first.
        </p>
      </Dialog>
    )

  const legend = 'text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]'
  const box = 'grid gap-2 border border-[var(--color-border)] p-3'

  return (
    <Dialog open title={`Peripherals - ${terminal.name}`} onClose={onClose} footer={<Btn onClick={onClose}>Close</Btn>}>
      <p className="text-[12px] text-[var(--color-muted)]">
        Workstation <span style={mono}>{ws}</span>. Changes apply asynchronously
        {locked ? `; unlocks in ${cooldown.left}s` : ''}.
      </p>

      <fieldset className={box} style={{ borderRadius: 'var(--radius)' }}>
        <legend className={`px-1 ${legend}`}>Receipt printer</legend>
        {printer.isLoading ? (
          <Inline tone="muted">loading...</Inline>
        ) : printer.data?.hasPrinter ? (
          <div className="flex items-center justify-between gap-3 text-[12px]">
            <span>
              <Chip tone="ok">{printer.data.printerModel ?? 'configured'}</Chip>
              {printer.data.printerIp && (
                <span className="ml-2 text-[var(--color-muted)]" style={mono}>
                  {printer.data.printerIp}
                </span>
              )}
            </span>
            <Btn
              variant="danger"
              disabled={locked || unassignPrinter.isPending}
              onClick={() => unassignPrinter.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignPrinter.isPending ? 'Removing...' : 'Unassign'}
            </Btn>
          </div>
        ) : (
          <>
            <Select
              label="Printer model"
              value={model}
              onChange={setModel}
              options={PRINTER_MODELS}
              placeholder="Select a model"
            />
            {model === 'TM-88V' && (
              <Field label="Printer MAC">
                <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
              </Field>
            )}
            <div>
              <Btn
                variant="primary"
                disabled={!printerValid || locked || setupPrinter.isPending}
                onClick={() =>
                  setupPrinter.mutate(
                    {
                      code,
                      workstationId: ws,
                      printerModel: model,
                      printerMac: model === 'TM-88V' ? mac.trim() : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupPrinter.isPending ? 'Setting up...' : 'Set up printer'}
              </Btn>
            </div>
          </>
        )}
        {printer.error && <Inline tone="bad">{apiErrorMessage(printer.error)}</Inline>}
        {setupPrinter.error && <Inline tone="bad">{apiErrorMessage(setupPrinter.error)}</Inline>}
        {unassignPrinter.error && <Inline tone="bad">{apiErrorMessage(unassignPrinter.error)}</Inline>}
      </fieldset>

      <fieldset className={box} style={{ borderRadius: 'var(--radius)' }}>
        <legend className={`px-1 ${legend}`}>Cash drawer</legend>
        {drawer.isLoading ? (
          <Inline tone="muted">loading...</Inline>
        ) : drawer.data?.hasCashDrawer ? (
          <div className="flex items-center justify-between gap-3 text-[12px]">
            <Chip tone="ok">
              {`${drawer.data.drawerSize ?? 'configured'}${drawer.data.logicalName ? ` · ${drawer.data.logicalName}` : ''}`}
            </Chip>
            <Btn
              variant="danger"
              disabled={locked || unassignDrawer.isPending}
              onClick={() => unassignDrawer.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignDrawer.isPending ? 'Removing...' : 'Unassign'}
            </Btn>
          </div>
        ) : (
          <>
            <Select
              label="Drawer size"
              value={size}
              onChange={(v) => setSize(v as '' | 'large' | 'small')}
              options={[
                ['large', 'Large'],
                ['small', 'Small'],
              ]}
              placeholder="Select a size"
            />
            {size === 'large' && (
              <>
                <Select
                  label="Printer type"
                  value={ptype}
                  onChange={(v) => setPtype(v as '' | 'epson' | 'toshiba')}
                  options={[
                    ['epson', 'Epson'],
                    ['toshiba', 'Toshiba'],
                  ]}
                  placeholder="Printer type"
                />
                <Select
                  label="Logical name"
                  value={logical}
                  onChange={(v) => setLogical(v as typeof logical)}
                  options={LOGICAL.map((n) => [n, n])}
                />
              </>
            )}
            {size === 'small' && (
              <label className="flex items-start gap-2 text-[12px] text-[var(--color-muted)]">
                <input
                  type="checkbox"
                  className="mt-0.5 size-3.5 accent-[var(--color-accent)]"
                  checked={smallOk}
                  onChange={(e) => setSmallOk(e.target.checked)}
                />
                I confirmed the device number is "3" in POS Control Center.
              </label>
            )}
            {drawerRuleError && <Inline tone="warn">{drawerRuleError}</Inline>}
            <div>
              <Btn
                variant="primary"
                disabled={!drawerValid || locked || setupDrawer.isPending}
                onClick={() =>
                  size &&
                  setupDrawer.mutate(
                    {
                      code,
                      workstationId: ws,
                      drawerSize: size,
                      printerType: size === 'large' && ptype ? ptype : undefined,
                      logicalName: size === 'large' ? logical : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupDrawer.isPending ? 'Setting up...' : 'Set up cash drawer'}
              </Btn>
            </div>
          </>
        )}
        {drawer.error && <Inline tone="bad">{apiErrorMessage(drawer.error)}</Inline>}
        {setupDrawer.error && <Inline tone="bad">{apiErrorMessage(setupDrawer.error)}</Inline>}
        {unassignDrawer.error && <Inline tone="bad">{apiErrorMessage(unassignDrawer.error)}</Inline>}
      </fieldset>
    </Dialog>
  )
}

export function SignalPosTerminals() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const terminals = useLocationTerminals(idOrCode)
  const unassign = useUnassignTerminal()

  const [assignOpen, setAssignOpen] = useState(false)
  const [periph, setPeriph] = useState<SelectInternalTerminal | null>(null)
  const [removing, setRemoving] = useState<SelectInternalTerminal | null>(null)

  const crumbs = <SubCrumbs idOrCode={idOrCode} section="pos" page="terminals" />

  if (location.isLoading || terminals.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Terminals" />
        <div className="overflow-auto">
          <SkelPanels n={1} />
        </div>
      </div>
    )
  const loc = location.data
  if (location.error || terminals.error || !loc)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Terminals" />
        <Note error>Failed to load terminals: {(location.error ?? terminals.error)?.message ?? 'Not found.'}</Note>
      </div>
    )

  const list = terminals.data ?? []
  const storeCode = loc.aptosStoreCode ?? loc.code ?? idOrCode
  // Printer/drawer `code` is passed bare (the location code), as v1's terminals route does.
  const posCode = loc.code ?? storeCode

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="Terminals"
        meta={
          <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
            {list.length} assigned
          </span>
        }
        right={
          <Btn variant="primary" onClick={() => setAssignOpen(true)}>
            Assign terminal
          </Btn>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <Panel title="Payment terminals">
          {list.length === 0 ? (
            <Empty>No terminals assigned to this store.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse" aria-label="Terminals">
                <thead>
                  <tr>
                    {['Terminal', 'Model', 'Status', 'Workstation', 'IP', 'Firmware', ''].map((h) => (
                      <th key={h || 'actions'} scope="col" className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id} className="hover:bg-[var(--color-surface-2)]">
                      <td className={`${td} font-medium`}>{t.name}</td>
                      <td className={`${td} text-[var(--color-muted)]`}>{t.model ?? '-'}</td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1.5">
                          <Dot tone={termTone(t.status)} />
                          {t.status ?? 'unknown'}
                        </span>
                      </td>
                      <td className={td} style={mono}>
                        {t.workstationId ?? '-'}
                      </td>
                      <td className={td} style={mono}>
                        {t.ethernetIpAddress ?? t.wifiIpAddress ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                        {t.firmwareVersion ?? '-'}
                      </td>
                      <td className={`${td} text-right`}>
                        <span className="inline-flex gap-1">
                          <Btn onClick={() => setPeriph(t)}>Peripherals</Btn>
                          <Btn variant="danger" onClick={() => setRemoving(t)}>
                            Unassign
                          </Btn>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {assignOpen && (
        <AssignDialog
          idOrCode={idOrCode}
          storeCode={storeCode}
          banner={loc.banner ?? ''}
          assignedIds={new Set(list.map((t) => t.id))}
          onClose={() => setAssignOpen(false)}
        />
      )}
      {periph && <PeripheralsDialog code={posCode} terminal={periph} onClose={() => setPeriph(null)} />}
      <ConfirmDialog
        open={removing !== null}
        title={`Unassign ${removing?.name ?? 'terminal'}?`}
        body="Removes the terminal from this store so it can be assigned elsewhere. Printer and cash drawer setup is unchanged."
        confirmLabel="Unassign"
        danger
        pending={unassign.isPending}
        error={unassign.error ? apiErrorMessage(unassign.error) : null}
        onConfirm={() =>
          removing && unassign.mutate({ idOrCode, terminalId: removing.id }, { onSuccess: () => setRemoving(null) })
        }
        onClose={() => {
          unassign.reset()
          setRemoving(null)
        }}
      />
    </div>
  )
}
